// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A dialog component for assigning assets to projects.
 * This module provides a comprehensive interface for managing
 * asset assignments, including project selection, date validation,
 * and capacity tracking. It ensures assignments follow business
 * rules and maintains data consistency.
 *
 * Key features:
 * - Project selection
 * - Asset validation
 * - Date constraints
 * - Capacity tracking
 * - Error handling
 * - Real-time updates
 */
import { hasRangeViolationError } from '@/api/error-utils';
import { getAssetProjectHistory } from '@/asset/api/get-asset-project-history';
import { Asset, SparseAsset } from '@/asset/api/types';
import { AssetInventoryDateWarning } from '@/asset/components/asset-inventory-date-warning';
import { FormSparseAssetSearch } from '@/asset/components/form-sparse-asset-search';
import { FormDatePicker } from '@/components/form-date-picker';
import { SupportSnackbar } from '@/components/support-snackbar';
import { MINIMUM_DATE } from '@/constants/date-shortcut';
import { FormProjectSearch } from '@/project/components/form-project-search';
import { useAssetAssignment } from '@/project/hooks/use-asset-assignment';
import { dateSchema } from '@/utilities/date-time-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating asset data.
 * Uses passthrough since the asset comes from props and is already validated.
 */
const assetSchema = z.custom<SparseAsset>();

/**
 * Schema for validating project data.
 * Ensures the project exists and can accept assignments.
 */
const projectSchema = z.object(
  {
    id: z.string(),
    key: z.string(),
    name: z.string(),
    // Assignments field is optional since we now use sparse projects
  },
  { message: 'Project is required' }
);

/**
 * Schema for validating the assignment date.
 * Ensures a valid date is provided.
 */
const requiredDateSchema = dateSchema
  .nullable()
  .refine((date): date is DateTime => date !== null && date.isValid, {
    message: 'Effective date is required',
  })
  .refine((date) => date >= MINIMUM_DATE, {
    message: 'Effective date cannot be before January 1, 1970',
  });

/**
 * Interface for the raw form input data.
 * Represents the unvalidated state of the assignment form.
 *
 * @interface InputData
 */
interface InputData {
  /**
   * The asset to be assigned.
   * Can be null when no asset is selected.
   */
  asset: z.infer<typeof assetSchema> | null;

  /**
   * The project to assign the asset to.
   * Can be null when no project is selected.
   */
  project: z.infer<typeof projectSchema> | null;

  /**
   * The effective date of the assignment.
   * Can be null when no date is selected.
   */
  date: DateTime | null;
}

/**
 * Schema for validating form output data.
 * Enforces required fields and data format.
 */
const outputSchema = z
  .object({
    asset: assetSchema,
    project: projectSchema,
    date: requiredDateSchema,
  })
  .superRefine((data, context) => {
    const latestAllowedDate = DateTime.now().plus({ days: 1 }).endOf('day');

    if (data.date > latestAllowedDate) {
      context.addIssue({
        path: ['date'],
        code: z.ZodIssueCode.custom,
        message: 'Assets cannot be assigned in the future',
      });
    }
  });

/**
 * Type definition for validated form output.
 * Used when submitting the form data.
 *
 * @typedef {Object} OutputData
 */
type OutputData = z.infer<typeof outputSchema>;

/**
 * Props for configuring the AssetAssignmentDialog component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset to be assigned.
   * Used as the default value in the form.
   */
  asset: Asset;

  /**
   * If true, the dialog is open and visible.
   */
  isOpen: boolean;

  /**
   * Callback fired when the dialog requests to be closed.
   */
  onClose: () => void;
}

/**
 * A dialog component for assigning assets to projects.
 * Provides a form interface for selecting a project and
 * effective date, with validation and capacity tracking.
 *
 * Features:
 * - Project selection with search
 * - Date validation with constraints
 * - Capacity tracking display
 * - Form state management
 * - Error message display
 * - Real-time query invalidation
 *
 * Usage example:
 * ```tsx
 * function AssetView({ asset }: { asset: Asset }) {
 *   const [isOpen, setIsOpen] = useState(false);
 *
 *   return (
 *     <AssetAssignmentDialog
 *       asset={asset}
 *       isOpen={isOpen}
 *       onClose={() => setIsOpen(false)}
 *     />
 *   );
 * }
 * ```
 */
export function AssetAssignmentDialog(props: Props) {
  // Extract sparse asset fields from the full asset for form validation
  const sparseAsset = {
    id: props.asset.id,
    key: props.asset.key,
    serial: props.asset.serial,
    item: props.asset.item,
    functionalStatus: props.asset.functionalStatus,
    isInTransit: props.asset.isInTransit,
    inInventoryDate: props.asset.inInventoryDate,
  };

  const { control, reset, handleSubmit, setError } = useForm<
    InputData,
    undefined,
    OutputData
  >({
    resolver: zodResolver(outputSchema),
    defaultValues: { asset: sparseAsset, project: null, date: null },
  });

  const { assign, ...mutation } = useAssetAssignment({
    onSuccess: () => {
      close();
      reset();
    },
    onError: (error) => {
      if (hasRangeViolationError(error)) {
        setError('date', {
          type: 'manual',
          message:
            'An asset assignment already overlaps with this date. Please select a different date.',
        });
      }
    },
  });

  const date = useWatch({ control, name: 'date' });

  const assignmentQuery = useQuery({
    queryKey: ['asset', props.asset.key, 'project-history'],
    queryFn: () => getAssetProjectHistory(props.asset.key),
  });

  // Calculate the minimum allowed date based on previous assignments
  let minimumDate: DateTime | undefined = undefined;

  if (assignmentQuery.data && assignmentQuery.data.history.length > 0) {
    const lastAssignment =
      assignmentQuery.data.history[assignmentQuery.data.history.length - 1];

    if (lastAssignment.end) {
      minimumDate = lastAssignment.end;
    }
  }

  /**
   * Handles form submission when validation passes.
   * Creates the asset assignment with the selected project.
   */
  const handleValidSubmission: SubmitHandler<OutputData> = (data) => {
    assign(data.project.key, [{ id: props.asset.id, dateTime: data.date }]);
  };

  /**
   * Closes the dialog and resets the form state.
   * Restores default values for the next use.
   */
  const close = () => {
    if (mutation.isPending) return;

    reset({ asset: sparseAsset, project: null, date: null });
    props.onClose();
  };

  const showGenericError =
    mutation.isError && !hasRangeViolationError(mutation.error);

  return (
    <>
      <SupportSnackbar
        isOpen={showGenericError}
        message="An error occurred while assigning the asset."
        onClose={mutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Assign asset to project</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <FormSparseAssetSearch
              name="asset"
              label="Asset"
              control={control}
              readOnly
            />
            <Stack>
              <FormProjectSearch
                name="project"
                label="Project"
                control={control}
                disabled={mutation.isPending}
              />
            </Stack>
            <Stack direction="row" alignItems="center">
              <FormDatePicker
                disableFuture
                name="date"
                label="Effective date"
                control={control}
                disabled={mutation.isPending}
                minimumDate={minimumDate}
              />
              <AssetInventoryDateWarning asset={props.asset} dateTime={date} />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={close}>Cancel</Button>
          <Button
            type="submit"
            color="secondary"
            variant="contained"
            disabled={mutation.isPending}
          >
            Assign
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
